import React, { CSSProperties, ReactElement, ReactNode } from 'react';
import { BaseEntite, BaseEntiteStatique } from '../../../../common/src/entities';
import { ArrayHelpers, FieldArray, useField } from 'formik';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { BiChevronUp, BiLink, BiX } from 'react-icons/all';
import TooltipSimple from './TooltipSimple';
import { LinkContainer } from 'react-router-bootstrap';
import { PopoutEntite } from '../Dashboard/Utils/PopoutEntite';
import { useAlertDialog } from '../../Utils/dialogContext';
import { BiPencil } from 'react-icons/all';
import ChildFormControlFeedBack from './ChampFormulaire/ChildFormControlFeedBack';
import { reorder } from '../Dashboard/Thematiques/ChampMultipleDnd';

type Class<T = unknown> = new (...args: unknown[]) => T;

export type PropsDeChamp<TFormulaire, TEntite> = {
    id: keyof TFormulaire;
    titre?: ReactNode;
    children?: ReactNode;
    entite: Class<TEntite>;
    cacherTitre?: boolean;
    reorderable?: boolean;
    affichageElement: (
        value: TEntite | number,
        index: number,
        helpers: ArrayHelpers,
        affichageTexte: (value: TEntite | number) => ReactNode,
        disabled?: boolean,
    ) => ReactElement | null;
    affichageTexte?: (value: TEntite | number) => ReactNode;
    affichageAjout: (
        values: TEntite[],
        helpers: ArrayHelpers,
        maxSize?: number,
        title?: string,
        avecUnBouton?: boolean,
        appendedChildren?: ReactElement,
        onChange?: (value: TEntite) => void,
    ) => ReactElement;
    wrapperStyle?: CSSProperties;
    onRemove?: (index: number) => void;
    maxSize?: number;
    title?: string;
    details?: (index: number) => string;
    suggestions?: string[];
    labelCol?: number;
    avecUnBouton?: boolean;
    placeholder?: string;
    clickCrayon?: (index: number) => void;
    AffichageDetail?: (value: TEntite) => ReactElement | null;
    appendedChildren?: ReactElement;
    errorMessage?: string;
    isActif?: (value: TEntite | number, index: number) => boolean;
    onChange?: (value: TEntite) => void;
};

export const affichageElementDefaut = (entite: BaseEntiteStatique<BaseEntite>) =>
    function inner<TEntite extends BaseEntite>(
        value: TEntite | number,
        index: number,
        helpers: ArrayHelpers,
        affichageTexte: (value: TEntite | number) => ReactNode,
        disabled?: boolean,
    ): ReactElement {
        const { remove } = helpers;
        const texte: TEntite | number = (value as TEntite)?.id ? (value as TEntite) : (value as number);
        const cssColRow: CSSProperties = {
            height: '30px',
        };

        return (
            <Row style={cssColRow}>
                <Col className="d-flex align-items-center" style={cssColRow}>
                    {value && (
                        <LinkContainer to={`/dashboard/${entite?.key}/${(value as TEntite).id ?? (value as number)}`}>
                            <Button variant="link" size="sm" className="text-muted">
                                <BiLink size="1.5em" />
                            </Button>
                        </LinkContainer>
                    )}
                    {value && affichageTexte(texte)}
                </Col>
                <Col xs="auto">
                    {!disabled && (
                        <TooltipSimple texte="Supprimer">
                            <Button
                                variant="link"
                                className="d-none d-sm-flex align-items-center text-muted px-0"
                                onClick={() => remove(index)}
                            >
                                <BiX size="1.25em" />
                            </Button>
                        </TooltipSimple>
                    )}
                </Col>
            </Row>
        );
    };

function AffichageAjoutDefaut<TEntite extends BaseEntite>(props: {
    helpers: ArrayHelpers;
    values: TEntite[];
    entite: BaseEntiteStatique<TEntite>;
    maxSize: number;
    title: string;
    appendedChildren?: ReactElement;
    onChange?: (value: TEntite) => void;
}): ReactElement {
    const bsAlert = useAlertDialog();
    return (
        <PopoutEntite
            titre={`Ajouter un ${props.title?.toLowerCase() ?? props.entite.constructor.name.toLowerCase()}`}
            entite={props.entite}
            size="sm"
            disabled={props.maxSize === props.values.length ? true : false}
            onChange={(value) => {
                const entite = value as TEntite;
                if (entite) {
                    if (props.values.find((item) => item.id === entite.id)) {
                        bsAlert('Cet élément existe déja!', 'warning');
                    } else if (props.maxSize && props.values.length === props.maxSize) {
                        bsAlert(`Vous pouvez ajouter ${props.maxSize} élément(s)`, 'warning');
                    } else {
                        props.helpers.push(value);
                    }
                }
                if (props.onChange) props.onChange(entite);
            }}
            appendedChildren={props.appendedChildren}
        />
    );
}
export const affichageAjoutDefaut = (entite: BaseEntiteStatique<BaseEntite>) => (
    values,
    helpers,
    maxSize?,
    title?,
    appendedChildren?,
    onChange?,
) => (
    <AffichageAjoutDefaut
        helpers={helpers}
        values={values}
        entite={entite}
        maxSize={maxSize}
        title={title}
        appendedChildren={appendedChildren}
        onChange={onChange}
    />
);
export const affichageTexteDefaut = (value) => value.name;

/**
 * Utilisé lorsqu'on veut que ChampAutoCompletion ait un bouton de menu deroulant
 * séparé et a l'extérieur du champ. Affiche le label ainsi qu'un champ non éditable
 * qui n'accepteque les choix du menu.
 * Si on a un labelCol, c'est qu'on est dans le dashboard et on veut une seule
 * ligne avec le titre et le champ. On met alors aussi un champ plus compact (petit)
 * @param props
 * @returns le html du champ
 */
export default function ChampMultiple<TFormulaire, TEntite>(props: PropsDeChamp<TFormulaire, TEntite>): ReactElement {
    const {
        titre,
        id,
        children,
        cacherTitre,
        affichageElement,
        affichageAjout,
        affichageTexte = affichageTexteDefaut,
        wrapperStyle = {},
        onRemove,
        maxSize,
        title,
        details,
        suggestions,
        labelCol,
        avecUnBouton,
        clickCrayon,
        AffichageDetail,
        appendedChildren,
        errorMessage,
        isActif,
        onChange,
        reorderable,
    } = props;
    const [field, meta, helpers] = useField<TEntite[]>(String(id));
    const { setValue } = helpers;
    field.value;
    let fieldValue: TEntite[];
    if (Array.isArray(field.value)) {
        fieldValue = field.value;
    } else if (field.value) {
        fieldValue = [field.value];
    } else {
        fieldValue = [];
    }

    if (reorderable) {
        fieldValue = (fieldValue as any[]).sort((a, b) => {
            if (typeof a.ordreAffichage === 'undefined' && typeof b.ordreAffichage === 'undefined') return 0;
            if (typeof a.ordreAffichage === 'undefined' && typeof b.ordreAffichage !== 'undefined') return 1;
            if (typeof a.ordreAffichage !== 'undefined' && typeof b.ordreAffichage === 'undefined') return -1;
            if (a.ordreAffichage < b.ordreAffichage) return -1;
            return 1;
        });
    }

    let indexedFieldValue: { value: TEntite; newIndex: number }[] = fieldValue.map((value, newIndex) => ({
        value,
        newIndex,
    }));
    if (props.isActif && !reorderable) {
        indexedFieldValue.sort((a, b) => {
            const aActif = a.value?.['actif'];
            const bActif = b.value?.['actif'];
            if (!aActif && bActif) return 1;
            if (aActif && !bActif) return -1;
            return 0;
        });
    }

    const cssCardBody: React.CSSProperties = {
        padding: 0,
        minHeight: 30,
        height: '30px',
    };

    function ChampDetailsCrayon(index: number, value: TEntite): ReactElement {
        if (!(details && suggestions && clickCrayon && AffichageDetail)) return <></>;

        return (
            <Col style={{ display: 'flex', flexDirection: 'row', gap: '5px' }} xs={2} className="pr-0">
                {reorderable && index != 0 && (
                    <Button
                        variant="light"
                        size="sm"
                        onClick={() => {
                            // Set ordreAffichage if it was not yet defined
                            fieldValue.map((value, index) => ({
                                ...value,
                                ordreAffichage: index,
                            }));

                            const updatedField = [...fieldValue] as any[];

                            const reorderedItems = reorder(updatedField, index, index - 1);

                            // Update the local state with the reordered items
                            setValue(reorderedItems);

                            // Update the Formik field value with the reordered items
                            field.onChange({ target: { name: field.name, value: reorderedItems } });
                        }}
                    >
                        <BiChevronUp />
                    </Button>
                )}
                <Card style={{ width: '100%' }}>
                    <Card.Body style={cssCardBody} className="d-flex align-items-center">
                        <BiPencil
                            size="1.25em"
                            style={{ cursor: 'pointer', color: '#007bff' }}
                            onClick={() => clickCrayon(index)}
                            className="px-1"
                        />
                        {AffichageDetail(value)}
                    </Card.Body>
                </Card>
            </Col>
        );
    }

    function CarteChampVide(): ReactElement {
        return (
            <Card>
                <Card.Body style={cssCardBody} />
            </Card>
        );
    }

    return (
        <FieldArray name={field.name}>
            {function ChampMultipleInterne(arrayHelpers) {
                const helpers = {
                    ...arrayHelpers,
                    remove: function <T>(entite: number) {
                        if (onRemove) onRemove(entite);
                        return arrayHelpers.remove<T>(entite);
                    },
                };

                if (labelCol) {
                    return (
                        <Form.Group controlId={id as string}>
                            <Row>
                                <Col xs={labelCol}>{!cacherTitre && <Form.Label>{titre}</Form.Label>}</Col>
                                <Col>
                                    {indexedFieldValue.map(({ value, newIndex }) =>
                                        props.isActif?.(value, newIndex) ?? true ? (
                                            <Row key={newIndex}>
                                                {ChampDetailsCrayon(newIndex, value)}
                                                <Col>
                                                    <Card>
                                                        <Card.Body style={cssCardBody}>
                                                            <div style={wrapperStyle}>
                                                                <div key={newIndex}>
                                                                    {affichageElement(
                                                                        value,
                                                                        newIndex,
                                                                        helpers,
                                                                        affichageTexte,
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>
                                        ) : null,
                                    )}
                                    {fieldValue.length === 0 && <CarteChampVide />}
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={labelCol}></Col>
                                <Col className="pt-2">
                                    {affichageAjout(
                                        fieldValue,
                                        helpers,
                                        maxSize,
                                        title,
                                        avecUnBouton,
                                        appendedChildren,
                                        onChange,
                                    )}
                                </Col>
                            </Row>
                            {/* add a new function to display all*/}

                            <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                            <ChildFormControlFeedBack
                                messages={
                                    [errorMessage, meta.error].filter((msg: string | undefined) => !!msg) as string[]
                                }
                            />
                            {children}
                        </Form.Group>
                    );
                }

                return (
                    <Form.Group controlId={id as string}>
                        <Row>
                            <Col>{cacherTitre ? null : <Form.Label>{titre}</Form.Label>}</Col>
                            <Col xs="auto" className="pb-2">
                                {affichageAjout(
                                    fieldValue,
                                    helpers,
                                    maxSize,
                                    title,
                                    avecUnBouton,
                                    appendedChildren,
                                    onChange,
                                )}
                            </Col>
                        </Row>
                        <Card>
                            <Card.Body>
                                <div style={wrapperStyle}>
                                    {fieldValue.map((value, index) => (
                                        <div key={index}>{affichageElement(value, index, helpers, affichageTexte)}</div>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                        <ChildFormControlFeedBack
                            messages={[errorMessage, meta.error].filter((msg: string | undefined) => !!msg) as string[]}
                        />
                        {children}
                    </Form.Group>
                );
            }}
        </FieldArray>
    );
}
